import { useAuth, useEnduserTimelineItems, type TellescopeModelFromName } from "@tellescope/tanstack-query";
import { TimelineItem } from "./TimelineItem";
import { formatName } from "@tellescope/utils";
import { useTranslation } from "react-i18next";
import { AISummary, cn, PaginatedList, Spinner } from "@tellescope/ui";
import { useShallowTimelineSettingsStore } from "../../../../store";
import { toast } from "sonner";

export interface TimelineSectionProps {
  contact: TellescopeModelFromName<"endusers">;
  className?: string;
  summary?: string;
  summaryIsGenerating?: boolean;
}

export const TimelineSection = ({
  contact,
  className,
  summary,
  summaryIsGenerating: summaryIsGenerating,
}: TimelineSectionProps) => {
  const { t } = useTranslation("timeline");
  const { userSettings } = useShallowTimelineSettingsStore("userSettings");
  const { currentUser } = useAuth();
  const combineSMSEntries = userSettings[currentUser.id]?.combineAllSMSItems;
  const { i18n } = useTranslation();
  const { data, isLoading, fetchNextPage } = useEnduserTimelineItems({
    enduserId: contact.id,
    combineSMSEntries,
    locale: i18n.language,
    onOutOfCredits: () => {
      toast.error(t("You have run out of AI credits. Please purchase more to generate a summary for this timeline."));
    },
  });

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="text-center space-y-4">
          <div className="text-sm text-muted-foreground">{t("Loading timeline...")}</div>
        </div>
      </div>
    );
  }

  if (!data?.length) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="text-center space-y-4">
          <div className="text-sm text-muted-foreground">{t("No items found")}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col overflow-hidden gap-2">
      {!!summary || summaryIsGenerating ? (
        summary ? (
          <AISummary title={t("Interaction Summary")} content={summary} />
        ) : (
          <div className="flex gap-2 items-center justify-center w-full pb-4">
            <Spinner size="sm" color="ai" />
            <p className="text-sm text-muted-foreground">{t("Generating summary...")}</p>
          </div>
        )
      ) : null}
      <PaginatedList className={cn("flex flex-col gap-2 flex-grow", className)} onBottomReached={() => fetchNextPage()}>
        {data.map((item) => {
          const key = item._type === "form_response_group" ? `form-${item.formId}` : `${item._type}-${item.id}`;
          return (
            <TimelineItem
              key={key}
              currentEnduserId={contact.id}
              enduserName={formatName(contact)}
              item={item}
              className="mb-2"
            />
          );
        })}
      </PaginatedList>
    </div>
  );
};
